import React, { useState, useEffect } from 'react';
import { populationHealthApi } from '../../lib/api';
import AINarrativePanel from '../../components/AINarrativePanel';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Users, Activity, AlertTriangle, TrendingUp, Heart, BarChart3, Brain, Zap,
  Shield, RefreshCw, ChevronRight
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, Legend } from 'recharts';
import { toast } from 'sonner';

const COLORS = ['#0E7490', '#1A56DB', '#166534', '#92400E', '#991B1B'];

export default function PopulationHealthPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [predictions, setPredictions] = useState(null);
  const [predLoading, setPredLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await populationHealthApi.getAnalytics();
      setData(res.data);
    } catch (err) {
      console.error('Failed to load population health data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPredictions = async () => {
    setPredLoading(true);
    try {
      const res = await populationHealthApi.getPredictions();
      setPredictions(res.data.predictions);
    } catch (err) {
      toast.error('Failed to generate predictions');
    } finally {
      setPredLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return <div className="text-center py-8 text-[#64748B]">Failed to load data</div>;

  const riskData = [
    { name: 'High Risk', value: data.risk_stratification.high, fill: '#991B1B' },
    { name: 'Medium Risk', value: data.risk_stratification.medium, fill: '#92400E' },
    { name: 'Low Risk', value: data.risk_stratification.low, fill: '#166534' },
  ];

  const genderData = [
    { name: 'Male', value: data.gender_distribution.male },
    { name: 'Female', value: data.gender_distribution.female },
    { name: 'Other', value: data.gender_distribution.other },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-8" data-testid="population-health-page">
      <div>
        <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Population Health Analytics
        </h1>
        <p className="text-[#64748B] mt-1">
          Comprehensive view of patient population health metrics and trends
        </p>
      </div>

      <AINarrativePanel scope="population_health" contextData={data} />

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="careiq-metric-card" data-testid="total-patients-metric">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0E7490]/10 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-[#0E7490]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#0A2342]">{data.total_patients}</div>
                <div className="text-xs text-[#64748B]">Total Patients</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="careiq-metric-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#991B1B]/10 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-[#991B1B]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#991B1B]">{data.risk_stratification.high}</div>
                <div className="text-xs text-[#64748B]">High Risk Patients</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="careiq-metric-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#92400E]/10 rounded-lg flex items-center justify-center">
                <Heart className="w-5 h-5 text-[#92400E]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#0A2342]">{data.care_gaps_patients}</div>
                <div className="text-xs text-[#64748B]">Care Gaps</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="careiq-metric-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#166534]/10 rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-[#166534]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#0A2342]">{data.top_medications.length}</div>
                <div className="text-xs text-[#64748B]">Active Medications</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Risk Stratification */}
        <Card className="careiq-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#0A2342]">Risk Stratification</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={riskData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                  {riskData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Age Distribution */}
        <Card className="careiq-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#0A2342]">Age Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.age_distribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="group" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} />
                <Tooltip />
                <Bar dataKey="count" fill="#0E7490" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Gender Distribution */}
        <Card className="careiq-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#0A2342]">Gender Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={genderData} cx="50%" cy="50%" outerRadius={70} dataKey="value" label>
                  {genderData.map((entry, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top Medications */}
        <Card className="careiq-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#0A2342]">Top Medications</CardTitle>
          </CardHeader>
          <CardContent>
            {data.top_medications.length > 0 ? (
              <div className="space-y-3">
                {data.top_medications.map((med, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-[#F8FAFC]">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: COLORS[i % COLORS.length] }}>
                        {i + 1}
                      </div>
                      <span className="font-medium text-[#0A2342]">{med.name}</span>
                    </div>
                    <span className="text-sm text-[#64748B]">{med.count} patients</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-6 text-[#64748B]">No medication data</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Appointment Trends */}
      <Card className="careiq-card">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#0A2342]">30-Day Appointment Trends</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={data.appointment_trends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" stroke="#64748B" fontSize={10} tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
              <YAxis stroke="#64748B" fontSize={12} />
              <Tooltip />
              <Area type="monotone" dataKey="count" stroke="#0E7490" fill="#0E7490" fillOpacity={0.1} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* AI Predictions Section */}
      <Card className="careiq-card border-l-4 border-l-[#7C3AED]" data-testid="ai-predictions-section">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
            <Brain className="w-5 h-5 text-[#7C3AED]" />
            AI Population Health Predictions
            <span className="text-xs bg-[#7C3AED]/10 text-[#7C3AED] px-2 py-0.5 rounded-full">AI-Powered</span>
          </CardTitle>
          <Button
            className="bg-[#7C3AED] hover:bg-[#7C3AED]/90 text-white"
            onClick={loadPredictions}
            disabled={predLoading}
            data-testid="generate-predictions-btn"
          >
            {predLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Zap className="w-4 h-4 mr-1" />}
            {predLoading ? 'Analyzing...' : 'Generate Predictions'}
          </Button>
        </CardHeader>
        <CardContent>
          {predictions ? (
            <div className="space-y-6">
              {/* Summary */}
              {predictions.summary && (
                <div className="p-4 bg-[#7C3AED]/5 rounded-lg border border-[#7C3AED]/10">
                  <div className="text-sm font-medium text-[#0A2342]">{predictions.summary}</div>
                </div>
              )}

              <div className="grid lg:grid-cols-2 gap-4">
                {/* Readmission Risk */}
                {predictions.readmission_risk?.prediction && (
                  <div className="p-4 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]">
                    <h4 className="text-sm font-medium text-[#0A2342] flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-[#92400E]" /> Readmission Risk
                    </h4>
                    <div className="text-lg font-bold text-[#0A2342]">{predictions.readmission_risk.prediction}</div>
                    {predictions.readmission_risk.factors && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {predictions.readmission_risk.factors.map((f, i) => (
                          <span key={i} className="px-2 py-0.5 text-[10px] bg-[#92400E]/10 text-[#92400E] rounded">{f}</span>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-[#0E7490] mt-2">{predictions.readmission_risk.recommendation}</p>
                  </div>
                )}

                {/* Cost Predictions */}
                {predictions.cost_predictions?.trend && (
                  <div className="p-4 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]">
                    <h4 className="text-sm font-medium text-[#0A2342] flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4 text-[#1A56DB]" /> Cost Forecast
                    </h4>
                    <div className="text-lg font-bold text-[#0A2342] capitalize">{predictions.cost_predictions.trend} Trend</div>
                    <p className="text-xs text-[#64748B] mt-1">Driver: {predictions.cost_predictions.primary_driver}</p>
                    <p className="text-xs text-[#166534] mt-1">{predictions.cost_predictions.savings_opportunity}</p>
                  </div>
                )}
              </div>

              {/* Chronic Disease Forecast */}
              {predictions.chronic_disease_forecast?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-[#0A2342] mb-3">Chronic Disease Forecast (6-month)</h4>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {predictions.chronic_disease_forecast.map((cd, i) => (
                      <div key={i} className="p-3 rounded-lg border border-[#E2E8F0]">
                        <div className="font-medium text-sm text-[#0A2342]">{cd.condition}</div>
                        <div className={`text-xs mt-1 ${cd.trend === 'increasing' ? 'text-[#991B1B]' : cd.trend === 'decreasing' ? 'text-[#166534]' : 'text-[#64748B]'}`}>
                          Trend: {cd.trend} {cd.projected_patients_6mo && `(${cd.projected_patients_6mo} projected)`}
                        </div>
                        <p className="text-[10px] text-[#0E7490] mt-1">{cd.intervention}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Care Gap Analysis */}
              {predictions.care_gap_analysis?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-[#0A2342] mb-3">Care Gap Analysis</h4>
                  <div className="space-y-2">
                    {predictions.care_gap_analysis.map((gap, i) => (
                      <div key={i} className={`p-3 rounded-lg flex items-start gap-3 ${gap.priority === 'high' ? 'bg-[#991B1B]/5' : gap.priority === 'medium' ? 'bg-[#92400E]/5' : 'bg-[#F8FAFC]'}`}>
                        <span className={`text-[10px] font-bold uppercase mt-1 px-1.5 py-0.5 rounded ${gap.priority === 'high' ? 'bg-[#991B1B]/10 text-[#991B1B]' : gap.priority === 'medium' ? 'bg-[#92400E]/10 text-[#92400E]' : 'bg-[#64748B]/10 text-[#64748B]'}`}>{gap.priority}</span>
                        <div>
                          <div className="text-xs font-medium text-[#0A2342]">{gap.gap}</div>
                          <div className="text-[10px] text-[#64748B]">{gap.affected_patients_pct} of patients affected</div>
                          <div className="text-[10px] text-[#0E7490] mt-1">{gap.intervention}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Preventive Care */}
              {predictions.preventive_care_opportunities?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-[#0A2342] mb-3">Preventive Care Opportunities</h4>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {predictions.preventive_care_opportunities.map((pc, i) => (
                      <div key={i} className="p-3 rounded-lg bg-[#166534]/5 border border-[#166534]/10">
                        <div className="text-xs font-medium text-[#0A2342]">{pc.screening}</div>
                        <div className="text-[10px] text-[#64748B] mt-1">{pc.eligible_patients_pct} eligible</div>
                        <div className="text-[10px] text-[#166534] mt-1">{pc.impact}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="text-[10px] text-[#94A3B8] italic">AI-generated predictions for planning purposes. Clinical validation required.</div>
            </div>
          ) : (
            <div className="text-center py-8 text-[#64748B]">
              <Brain className="w-12 h-12 mx-auto mb-3 text-[#94A3B8]" />
              <p className="text-sm">Click "Generate Predictions" to get AI-powered population health insights</p>
              <p className="text-xs text-[#94A3B8] mt-1">Analyzes patient demographics, risk scores, medications, and appointment data</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
